/**
 * Terms of Service page - CHAP-134 (BLOCKS-CUTOVER).
 *
 * Defensible v1 baseline. Formal Cooley Go review required before
 * production launch - language here covers the standard SaaS shape
 * with chapter-build-specific clauses (tenant data sovereignty,
 * capital-project data sensitivity, TX governing law).
 *
 * Per the GDPR-flavored posture in `gdpr-ccpa-scope-decision.md`.
 */

import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = '2026-05-27';

export default function Terms() {
  return (
    <article
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '40px 24px',
        fontFamily: 'system-ui, sans-serif',
        color: '#03244d',
        lineHeight: 1.6,
      }}
    >
      <h1 style={{ fontSize: 32, marginBottom: 8 }}>Terms of Service</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 32 }}>
        Effective: {EFFECTIVE_DATE} · 1116 LLC dba ChapterBuild
      </p>

      <p>
        These Terms of Service ("Terms") form a binding agreement between you and
        <strong> 1116 LLC</strong> (a Texas limited liability company), doing business
        as <strong>ChapterBuild</strong> ("we," "us," or "ChapterBuild"), governing your
        use of the ChapterBuild service. By creating an account or using the service,
        you agree to these Terms.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>1. What ChapterBuild is</h2>
      <p>
        ChapterBuild is a multi-tenant SaaS platform that helps fraternity and
        sorority chapters plan and execute capital projects - new houses,
        renovations, fundraising campaigns. Each chapter is a separate "tenant"
        in the system; tenants do not see each other's data.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>2. Accounts and tenants</h2>
      <p>
        To use ChapterBuild, you must be a member of an authorized tenant
        (typically a Cabinet member, Board member, voter, or observer in a Greek-
        life chapter). Your tenant administrator controls who joins the tenant
        and at what role and tier.
      </p>
      <p>
        You agree to keep your account credentials confidential. You're responsible
        for activity that happens through your account. If you suspect unauthorized
        use, contact us immediately at <a href="mailto:support@chapterbuild.com">support@chapterbuild.com</a>.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>3. Your tenant's data, your tenant's control</h2>
      <p>
        Your tenant retains ownership of the data uploaded by tenant members
        (the "Tenant Data"). We process Tenant Data on your tenant's behalf as
        described in our{' '}
        <Link to="/legal/privacy" style={{ color: '#03244d' }}>
          Privacy Policy
        </Link>{' '}
        and any Data Processing Agreement (DPA) signed between your tenant and
        us. We do not sell Tenant Data, share it with non-subprocessors, or use
        it to train AI models or for any purpose beyond delivering the service.
      </p>
      <p>
        On termination of your tenant's subscription, Tenant Data is retained
        for 30 days (to allow for accidental cancellation recovery) and then
        deleted, except for audit logs and content held under legal hold.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>4. Sensitive content</h2>
      <p>
        ChapterBuild holds materials that can be sensitive: donor capacity
        ratings, alumni databases with personal contact information, recorded
        Cabinet meetings, and confidential financial models. You agree to:
      </p>
      <ul>
        <li>
          Use sensitivity tier classifications (public / restricted / confidential
          / highly confidential) to gate access appropriately
        </li>
        <li>
          Not upload content that violates third-party privacy rights without
          consent
        </li>
        <li>
          Not upload health information, government identifiers, payment card
          numbers, or other "high-risk" PII categories that are out of scope for
          ChapterBuild's intended use
        </li>
        <li>
          Not use the service for activity prohibited by your chapter's national
          organization or by your university's policies
        </li>
      </ul>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>5. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Attempt to access another tenant's data without authorization</li>
        <li>Probe, scan, or test the vulnerability of the service or related infrastructure</li>
        <li>Use the service to send spam, phishing, or other unsolicited communications</li>
        <li>Reverse-engineer, decompile, or disassemble the service</li>
        <li>Resell or sublicense the service without our prior written consent</li>
        <li>Interfere with or disrupt the service or attempt to gain unauthorized access</li>
      </ul>
      <p>
        We may suspend access to your account or tenant if we reasonably believe
        you've violated these acceptable-use rules. We'll let you (or your tenant
        administrator) know what's happening and give you a chance to remediate
        unless the violation is severe enough that immediate action is required
        (e.g., active security threat).
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>6. Subscriptions and fees</h2>
      <p>
        ChapterBuild is offered on a per-tenant subscription basis. Pricing is
        described on{' '}
        <a href="https://chapterbuild.com" style={{ color: '#03244d' }}>
          chapterbuild.com
        </a>{' '}
        or in your tenant's order form. Fees are billed in advance per subscription
        period. We may change pricing for future renewal terms with 30 days' notice.
      </p>
      <p>
        Payment processing is handled by Stripe (US-based). We do not store
        payment card numbers ourselves.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>7. Cancellation and termination</h2>
      <p>
        Your tenant administrator can cancel the subscription at any time via
        the billing portal. Cancellation takes effect at the end of the current
        billing period; no prorated refund for partial periods.
      </p>
      <p>
        We may terminate or suspend your account or your tenant's subscription
        immediately for material breach of these Terms (including the acceptable-
        use rules above), legal compliance requirements, or non-payment after a
        reasonable notice and cure period.
      </p>
      <p>
        On termination, you can export Tenant Data via your tenant administrator
        for up to 30 days, after which data is deleted as described above.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>8. Service availability</h2>
      <p>
        We aim for 99.5% uptime, measured monthly. ChapterBuild is built on
        reputable cloud infrastructure (Render, Cloudflare, AWS-equivalent
        services); we don't operate our own data centers. Maintenance windows
        and incidents will be communicated through our{' '}
        <a href="mailto:support@chapterbuild.com" style={{ color: '#03244d' }}>
          support email
        </a>
        .
      </p>
      <p>
        We don't offer service-level credits for downtime in this v1 of the
        service. If a sustained outage materially affects your tenant, contact
        us and we'll work in good faith to make it right.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>9. Confidentiality</h2>
      <p>
        Each party will protect the other's Confidential Information using at
        least the same degree of care it uses for its own confidential information
        of similar importance, and at minimum reasonable care. Confidential
        Information doesn't include information that's publicly available, was
        already known, or is independently developed.
      </p>
      <p>
        Your Tenant Data is your Confidential Information; our service and
        underlying technology are our Confidential Information.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
        ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. We do not warrant
        that the service will be uninterrupted, error-free, or completely secure.
      </p>
      <p>
        ChapterBuild is a tool to help your chapter manage capital project work.
        Decisions made using ChapterBuild data (donor outreach, financial model
        approval, vote outcomes) are your chapter's responsibility. We are not a
        financial advisor, legal advisor, or fiduciary.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>11. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY IS LIABLE FOR
        INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
        LOST PROFITS, REVENUE, OR DATA. EACH PARTY'S TOTAL LIABILITY UNDER
        THESE TERMS IS LIMITED TO THE FEES PAID BY YOUR TENANT TO US IN THE
        12 MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM (or $1,000,
        whichever is greater).
      </p>
      <p>
        These limitations don't apply to indemnification obligations, breach
        of confidentiality, willful misconduct, or violation of intellectual
        property rights.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>12. Indemnification</h2>
      <p>
        You agree to indemnify and hold us harmless from any claims arising
        from (a) your tenant's content or use of the service in violation of
        these Terms, (b) your tenant's violation of any third-party right
        (including a member's privacy rights), or (c) misuse of donor or alumni
        data by your tenant members.
      </p>
      <p>
        We agree to indemnify your tenant against third-party claims that the
        service (as provided by us, unmodified) infringes a third party's US
        patent, copyright, or trademark.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>13. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of the State of Texas, without
        regard to conflict-of-laws rules. Any dispute will be resolved in the
        state or federal courts located in Travis County, Texas, and both
        parties consent to that jurisdiction.
      </p>
      <p>
        Before filing any lawsuit, the parties will attempt in good faith to
        resolve the dispute informally for at least 30 days. Either party may
        seek injunctive relief at any time without first attempting informal
        resolution where reasonably necessary to protect intellectual property
        or confidential information.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>14. Changes to these Terms</h2>
      <p>
        We may update these Terms occasionally. Material changes will be
        communicated by email to tenant administrators at least 30 days before
        they take effect (unless required sooner by law). Continued use of the
        service after the effective date constitutes acceptance.
      </p>

      <h2 style={{ fontSize: 20, marginTop: 32 }}>15. Miscellaneous</h2>
      <ul>
        <li>
          <strong>Entire agreement</strong> - These Terms (together with the
          Privacy Policy and any signed DPA or order form) are the entire
          agreement between you and us regarding the service.
        </li>
        <li>
          <strong>Assignment</strong> - You can't assign these Terms without
          our consent. We may assign these Terms to an affiliate or successor
          in connection with a corporate transaction.
        </li>
        <li>
          <strong>Severability</strong> - If any provision is unenforceable,
          the rest remains in effect.
        </li>
        <li>
          <strong>Waiver</strong> - Failure to enforce a provision is not a
          waiver of the right to enforce it later.
        </li>
        <li>
          <strong>Notices</strong> - Email is the primary notice method;
          ChapterBuild's notice address is{' '}
          <a href="mailto:legal@chapterbuild.com" style={{ color: '#03244d' }}>
            legal@chapterbuild.com
          </a>
          ; your tenant's notice address is the email of the tenant's
          designated administrator.
        </li>
      </ul>

      <hr style={{ margin: '32px 0', borderColor: '#e5e7eb' }} />
      <p style={{ color: '#6b7280', fontSize: 14 }}>
        See also our{' '}
        <Link to="/legal/privacy" style={{ color: '#03244d' }}>
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link to="/legal/cookies" style={{ color: '#03244d' }}>
          cookie preferences
        </Link>
        .
      </p>
    </article>
  );
}
